import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  GiftIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  TruckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { ListPageSkeleton } from '../ui/PageSkeleton';
import EmptyState from '../ui/EmptyState';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import ActionOutcomePanel from '../ui/ActionOutcomePanel';
import ViewState from '../ui/ViewState';
import { useConfirmDialog } from '../ui/ConfirmDialog';

function PendingGiftsView({ role = 'vendor', onPendingCountChange, companyFilter = null, requestCompanyId = null }) {
  const { t, formatMessage } = useLanguage();
  const pg = t.pendingGifts;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [actionOutcome, setActionOutcome] = useState(null);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const isAdmin = role === 'admin';
  const listEndpoint = isAdmin ? '/api/admin/pending-gifts' : '/api/vendor/pending-gifts';

  const fetchGifts = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const config = { withCredentials: true };
      if (requestCompanyId) {
        config.params = { company_id: requestCompanyId };
      }
      const response = await axios.get(listEndpoint, config);
      setData(response.data);
    } catch (error) {
      console.error('Error loading pending gifts:', error);
      setLoadError(pg.failedToLoad);
      toast.error(pg.failedToLoad);
    } finally {
      setLoading(false);
    }
  }, [listEndpoint, pg.failedToLoad, requestCompanyId]);

  const filterClaims = (claims = []) => {
    // When requestCompanyId already scopes the API, skip redundant client filter.
    if (!companyFilter || requestCompanyId) return claims;
    return claims.filter(
      (claim) => claim.company_id != null
        && String(claim.company_id) === String(companyFilter),
    );
  };

  useEffect(() => {
    fetchGifts();
  }, [fetchGifts]);

  const handleAdminDeliver = async (claim) => {
    const confirmed = await confirm({
      title: pg.confirmAdminDeliverTitle,
      message: pg.confirmAdminDeliverMessage,
      confirmLabel: pg.markDeliveredToVendor,
      confirmVariant: 'primary',
    });
    if (!confirmed) return;

    setActionId(claim.id);
    try {
      await axios.post(
        `/api/admin/pending-gifts/${claim.id}/deliver-to-vendor`,
        {},
        requestCompanyId
          ? { withCredentials: true, params: { company_id: requestCompanyId } }
          : { withCredentials: true },
      );
      toast.success(pg.adminDeliveredSuccess);
      const outcomeCopy = t.ux?.actionOutcome?.giftAdminDelivered;
      setActionOutcome({
        id: `admin-deliver-${claim.id}-${Date.now()}`,
        title: outcomeCopy?.title || pg.adminDeliveredSuccess,
        description: formatMessage(pg.claimedByLine, { name: claim.client_username || '—' }),
        nextStep: outcomeCopy?.nextStep,
      });
      await fetchGifts();
      onPendingCountChange?.();
    } catch (error) {
      toast.error(error.response?.data?.error || pg.actionFailed);
    } finally {
      setActionId(null);
    }
  };

  const handleVendorDeliver = async (claim) => {
    const confirmed = await confirm({
      title: pg.confirmVendorDeliverTitle,
      message: pg.confirmVendorDeliverMessage,
      confirmLabel: pg.markDeliveredToClient,
      confirmVariant: 'primary',
    });
    if (!confirmed) return;

    setActionId(claim.id);
    try {
      await axios.post(
        `/api/vendor/pending-gifts/${claim.id}/deliver`,
        {},
        { withCredentials: true }
      );
      toast.success(pg.vendorDeliveredSuccess);
      const outcomeCopy = t.ux?.actionOutcome?.giftVendorDelivered;
      setActionOutcome({
        id: `vendor-deliver-${claim.id}-${Date.now()}`,
        title: outcomeCopy?.title || pg.vendorDeliveredSuccess,
        description: claim.reward_name,
        nextStep: outcomeCopy?.nextStep,
      });
      await fetchGifts();
      onPendingCountChange?.();
    } catch (error) {
      toast.error(error.response?.data?.error || pg.actionFailed);
    } finally {
      setActionId(null);
    }
  };

  const renderClaimCard = (claim, { showAdminAction, showVendorAction, showWaitingBadge = false }) => {
    const showVendorDeadline = !isAdmin && claim.status === 'pending_vendor';
    const showVendorFutureDeadline = !isAdmin && claim.status === 'pending_admin';
    const showOverdueWarning = showVendorDeadline && claim.vendor_is_overdue_warning;

    return (
    <motion.article
      key={claim.id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`card-premium p-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between ${
        showOverdueWarning ? 'ring-2 ring-error-500/60' : ''
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <div className="icon-tile h-11 w-11 shrink-0 from-orange-500 to-amber-500">
            <GiftIcon className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-secondary-900 ">
              {claim.reward_name}
            </h3>
            <p className="mt-1 text-sm text-secondary-600 ">
              {formatMessage(pg.claimedByLine, { name: claim.client_username || '—' })}
            </p>
            {claim.company_name ? (
              <p className="mt-1 text-xs font-medium text-orange-600 ">{claim.company_name}</p>
            ) : null}
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-secondary-500 ">
              <span className="inline-flex items-center gap-1">
                <UserIcon className="h-3.5 w-3.5" />
                {claim.vendor_username || pg.noVendorAssigned}
              </span>
              <span>{claim.points_spent} {pg.pointsLabel}</span>
            </div>
            {showWaitingBadge ? (
              <span className="mt-3 inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800  ">
                {pg.waitingForAdminBadge}
              </span>
            ) : null}
            {showVendorFutureDeadline ? (
              <p className="mt-3 text-sm text-secondary-600 ">
                {formatMessage(pg.vendorFutureDeadline, { days: claim.vendor_delivery_days ?? 7 })}
              </p>
            ) : null}
            {showVendorDeadline ? (
              <div className="mt-3 space-y-2">
                <p className="text-sm font-medium text-secondary-800 ">
                  {claim.vendor_is_past_deadline
                    ? pg.vendorPastDeadline
                    : formatMessage(pg.vendorDaysRemaining, { days: claim.vendor_days_remaining ?? 0 })}
                </p>
                {showOverdueWarning ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-error-100 px-3 py-1 text-xs font-semibold text-error-700  ">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    {formatMessage(pg.vendorOverdueWarning, { day: claim.vendor_overdue_day ?? 5 })}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {showAdminAction && claim.can_admin_deliver ? (
        <Button
          disabled={actionId === claim.id}
          loading={actionId === claim.id}
          onClick={() => handleAdminDeliver(claim)}
          size="compact"
          className="w-full shrink-0 sm:w-auto"
        >
          {pg.markDeliveredToVendor}
        </Button>
      ) : null}

      {showVendorAction && claim.can_vendor_deliver ? (
        <Button
          disabled={actionId === claim.id}
          loading={actionId === claim.id}
          onClick={() => handleVendorDeliver(claim)}
          size="compact"
          className="w-full shrink-0 sm:w-auto"
        >
          {pg.markDeliveredToClient}
        </Button>
      ) : null}

      {!showAdminAction && !showVendorAction && (claim.status === 'completed' || claim.status === 'vendor_delivered') ? (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-success-100 px-3 py-1.5 text-sm font-medium text-success-700  ">
          <CheckCircleIcon className="h-4 w-4" />
          {claim.status === 'vendor_delivered' ? pg.awaitingClientBadge : pg.completed}
        </span>
      ) : null}
    </motion.article>
    );
  };

  if (loading) {
    return <ListPageSkeleton rows={5} />;
  }

  if (loadError) {
    return (
      <div className="space-y-6 xl:space-y-7">
        <PageHeader title={pg.title} subtitle={isAdmin ? pg.adminSubtitle : pg.vendorSubtitle} icon={GiftIcon} />
        <ViewState error={loadError} onRetry={fetchGifts} />
      </div>
    );
  }

  const pendingAdmin = filterClaims(data?.pending_admin || []);
  const pendingVendorAdminView = filterClaims(data?.pending_vendor || []);
  const awaitingAdmin = filterClaims(data?.awaiting_admin || []);
  const pendingVendor = filterClaims(data?.pending || []);
  const awaitingClient = filterClaims(data?.awaiting_client || []);
  const completed = filterClaims(data?.completed || []);

  const adminPendingCount = data?.pending_admin_count ?? pendingAdmin.length;
  const vendorAwaitingCount = data?.awaiting_admin_count ?? awaitingAdmin.length;
  const vendorPendingCount = data?.pending_count ?? pendingVendor.length;
  const vendorAwaitingClientCount = data?.awaiting_client_count ?? awaitingClient.length;

  return (
    <div className="space-y-6 xl:space-y-7">
      <PageHeader
        title={pg.title}
        subtitle={isAdmin ? pg.adminSubtitle : pg.vendorSubtitle}
        icon={GiftIcon}
      />

      {confirmDialog}

      <ActionOutcomePanel
        outcome={actionOutcome}
        onDismiss={() => setActionOutcome(null)}
      />

      {isAdmin ? (
        <>
          <section>
            <div className="mb-4 flex items-center gap-2">
              <ClockIcon className="h-5 w-5 text-orange-500" />
              <h2 className="text-lg font-semibold text-secondary-900 ">
                {pg.awaitingHandoff} ({adminPendingCount})
              </h2>
            </div>
            {pendingAdmin.length > 0 ? (
              <div className="space-y-3">
                {pendingAdmin.map((claim) =>
                  renderClaimCard(claim, { showAdminAction: true, showVendorAction: false })
                )}
              </div>
            ) : (
              <EmptyState
                icon={TruckIcon}
                title={pg.noAwaitingHandoff}
                description={pg.noAwaitingHandoffDesc}
              />
            )}
          </section>

          {pendingVendorAdminView.length > 0 ? (
            <section>
              <div className="mb-4 flex items-center gap-2">
                <TruckIcon className="h-5 w-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-secondary-900 ">
                  {pg.withVendor}
                </h2>
              </div>
              <div className="space-y-3">
                {pendingVendorAdminView.map((claim) =>
                  renderClaimCard(claim, { showAdminAction: false, showVendorAction: false })
                )}
              </div>
            </section>
          ) : null}
        </>
      ) : (
        <>
          {awaitingAdmin.length > 0 ? (
            <section className="mb-8">
              <div className="mb-4 flex items-center gap-2">
                <ClockIcon className="h-5 w-5 text-amber-500" />
                <h2 className="text-lg font-semibold text-secondary-900 ">
                  {pg.awaitingAdminHandoff} ({vendorAwaitingCount})
                </h2>
              </div>
              <p className="mb-3 text-sm text-secondary-600 ">
                {pg.awaitingAdminHandoffDesc}
              </p>
              <div className="space-y-3">
                {awaitingAdmin.map((claim) =>
                  renderClaimCard(claim, {
                    showAdminAction: false,
                    showVendorAction: false,
                    showWaitingBadge: true,
                  })
                )}
              </div>
            </section>
          ) : null}

          <section>
            <div className="mb-4 flex items-center gap-2">
              <ClockIcon className="h-5 w-5 text-orange-500" />
              <h2 className="text-lg font-semibold text-secondary-900 ">
                {pg.readyToDeliver} ({vendorPendingCount})
              </h2>
            </div>
            {pendingVendor.length > 0 ? (
              <div className="space-y-3">
                {pendingVendor.map((claim) =>
                  renderClaimCard(claim, { showAdminAction: false, showVendorAction: true })
                )}
              </div>
            ) : (
              <EmptyState
                icon={GiftIcon}
                title={pg.noPendingGifts}
                description={pg.noPendingGiftsDesc}
              />
            )}
          </section>

          {awaitingClient.length > 0 ? (
            <section>
              <div className="mb-4 flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-success-500" />
                <h2 className="text-lg font-semibold text-secondary-900 ">
                  {pg.awaitingClientConfirmation} ({vendorAwaitingClientCount})
                </h2>
              </div>
              <p className="mb-3 text-sm text-secondary-600 ">
                {pg.awaitingClientConfirmationDesc}
              </p>
              <div className="space-y-3">
                {awaitingClient.map((claim) =>
                  renderClaimCard(claim, { showAdminAction: false, showVendorAction: false })
                )}
              </div>
            </section>
          ) : null}
        </>
      )}

      {completed.length > 0 ? (
        <section>
          <div className="mb-4 flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-success-500" />
            <h2 className="text-lg font-semibold text-secondary-900 ">
              {pg.recentlyCompleted}
            </h2>
          </div>
          <div className="space-y-3">
            {completed.slice(0, 10).map((claim) =>
              renderClaimCard(claim, { showAdminAction: false, showVendorAction: false })
            )}
          </div>
        </section>
      ) : null}
    </div>
  );
}

export default PendingGiftsView;
